import { useCallback, useEffect, useRef, useState } from 'react';
import axios, { isAxiosError } from 'axios';
import { useOfflineStatus } from '@/hooks/use-offline';
import { apiClient, getApiBaseUrlEnv, getCsrfToken } from '@/lib/api-client';
import { enqueueSignature } from '@/lib/offline/outbox';
import { getCachedSigningToken } from '@/lib/offline/shift-signing-token-cache';
import { storeKioskCredential } from '@/lib/offline/kiosk-credential-vault';

export interface KioskSignResult {
  signer_name: string;
  /** ISO capture time; empty when the signature is queued offline. */
  signed_at: string;
  signature_line_id: number;
  signer_user_id?: number;
  cert_warning?: string;
  /** True when captured offline and awaiting sync (no server confirmation yet). */
  queued?: boolean;
}

interface UseKioskSignOptions {
  /** Whether the organization forces PIN entry for kiosk signing. */
  pinMode?: boolean;
  /** Document id (JSA or toolbox talk id) used for the offline outbox. */
  documentId: number;
  /** Signature line id for the resulting KioskSignResult (used by JSA). */
  signatureLineId?: number;
  /** Kiosk sign endpoint (e.g. /api/v1/safety/jsas/1/kiosk-sign/). */
  endpoint: string;
  /** Extra body fields merged into the request (e.g. signature_line_id, kit_inspection). */
  bodyBase?: Record<string, unknown>;
  /** Called with the result after a successful online or queued offline sign. */
  onSuccess: (result: KioskSignResult) => void;
}

export function useKioskSign({
  pinMode = false,
  documentId,
  signatureLineId = 0,
  endpoint,
  bodyBase = {},
  onSuccess,
}: UseKioskSignOptions) {
  const { isOffline } = useOfflineStatus();
  const usePinField = pinMode || isOffline;

  const [username, setUsername] = useState('');
  const [credential, setCredential] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Clear the credential whenever the PIN/password mode flips, so a value
  // typed for one field type doesn't leak into the other.
  const prevUsePinFieldRef = useRef(usePinField);
  useEffect(() => {
    if (prevUsePinFieldRef.current !== usePinField) {
      setCredential('');
      prevUsePinFieldRef.current = usePinField;
    }
  }, [usePinField]);

  const credentialsComplete = usePinField
    ? Boolean(username && credential.length === 6)
    : Boolean(username && credential);

  const clear = useCallback(() => {
    setUsername('');
    setCredential('');
    setSignatureData('');
    setError(null);
    setSubmitting(false);
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      setError(null);
      setSubmitting(true);

      try {
        if (usePinField && credential.length !== 6) {
          setError('Please enter a 6-digit PIN.');
          setSubmitting(false);
          return;
        }

        if (isOffline) {
          const cached = await getCachedSigningToken(username);
          if (!cached) {
            setError(
              "You're offline and don't have a cached signing token. A shift supervisor must check you in while online to enable offline signing.",
            );
            setSubmitting(false);
            return;
          }

          const idempotencyKey =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
              ? crypto.randomUUID()
              : `kiosk-${Date.now()}-${Math.random().toString(36).slice(2)}`;

          // The PIN is held only in memory and is cleared as soon as the sync
          // layer has sent it. The queued IndexedDB record never contains the PIN.
          storeKioskCredential(idempotencyKey, { pin: credential });

          await enqueueSignature({
            jsaId: documentId,
            endpoint,
            body: {
              ...bodyBase,
              username,
              signature_data: signatureData,
            },
            capturedAt: Date.now(),
            idempotencyKey,
          });

          const queuedResult: KioskSignResult = {
            signer_name: username,
            signed_at: '',
            signature_line_id: signatureLineId,
            queued: true,
          };

          clear();
          onSuccess(queuedResult);
          return;
        }

        const body = usePinField
          ? { ...bodyBase, username, pin: credential, signature_data: signatureData }
          : { ...bodyBase, username, password: credential, signature_data: signatureData };

        // Use a bare axios instance for kiosk sign-in. The backend returns 401
        // for invalid technician credentials, and apiClient's auth-refresh
        // interceptor would misread that as the supervisor's session expiring.
        const baseUrl =
          apiClient.defaults.baseURL ??
          (getApiBaseUrlEnv() ||
            (import.meta.env.PROD ? 'https://api.h3ight.com' : 'http://localhost:8000'));
        const csrf = getCsrfToken();
        const headers: Record<string, string> = {};
        if (csrf) {
          headers['X-CSRFToken'] = csrf;
        }

        const response = await axios.post<{
          success: boolean;
          signed_by: string;
          signer_user_id?: number;
          timestamp: string;
          cert_warning?: string;
          match_confidence?: string;
        }>(`${baseUrl}${endpoint}`, body, { withCredentials: true, headers });

        const result: KioskSignResult = {
          signer_name: response.data.signed_by,
          signed_at: response.data.timestamp,
          signature_line_id: signatureLineId,
          signer_user_id: response.data.signer_user_id,
          cert_warning: response.data.cert_warning,
        };

        clear();
        onSuccess(result);
      } catch (err) {
        if (isAxiosError(err) && !err.response) {
          setError("Can't connect — please check your network and try again.");
        } else if (
          isAxiosError<{ error?: string; detail?: string }>(err) &&
          err.response?.data?.error
        ) {
          setError(err.response.data.error);
        } else if (
          isAxiosError<{ detail?: string }>(err) &&
          err.response?.data?.detail
        ) {
          setError(err.response.data.detail);
        } else if (isAxiosError(err) && err.response?.status === 400) {
          setError('Invalid request. Please try again.');
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [
      bodyBase,
      clear,
      credential,
      documentId,
      endpoint,
      isOffline,
      onSuccess,
      signatureData,
      signatureLineId,
      usePinField,
      username,
    ],
  );

  return {
    isOffline,
    usePinField,
    username,
    setUsername,
    credential,
    setCredential,
    signatureData,
    setSignatureData,
    credentialsComplete,
    error,
    submitting,
    handleSubmit,
    clear,
  };
}
